/* src/components/events/ManageGuestsPage.tsx */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkInput from '../common/SparkInput';
import SparkHeading from '../common/SparkHeading';
import SparkFooter from '../common/SparkFooter';

import DeviceContactPicker from '../contacts/DeviceContactPicker';
import ImportRecipients from '../invitations/ImportRecipients';
import ManualEntryForm from '../invitations/ManualEntryForm';

// Add paywall imports
import useEventPaywall from '../../hooks/useEventPaywall';
import UnlockSalesModal from '../billing/UnlockSalesModal';

// Firestore and Functions imports
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  getDocs,
  Query,
  limit
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';

// ----------------------------------------------------------------
// TYPES AND INTERFACES
// ----------------------------------------------------------------
interface Recipient {
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  status: string;
  sent?: string; // e.g., "2 days ago"
  peopleCount?: number;  // Add this
}

interface EventData {
  title: string;
  date: string;
  location: string;
  [key: string]: any; // additional Firestore fields
}

// The expected structure from the Cloud Function response
interface SendInvitationsResponse {
  invitationIds?: string[];
}

// ----------------------------------------------------------------
// NEW: Interface for Invitation Document Type
// ----------------------------------------------------------------
interface InvitationDocument {
  recipientName?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  status?: string;
  sentAt?: { seconds: number };
  peopleCount?: number;  // Add this
}

// ----------------------------------------------------------------
// NEW: Type for Filter Statuses
// ----------------------------------------------------------------
type FilterStatus = 'all' | 'pending' | 'sent' | 'viewed' | 'responded' | 'accepted' | 'declined';

// ----------------------------------------------------------------
// COMPONENT
// ----------------------------------------------------------------
const ManageGuestsPage = () => {
  const { eventId } = useParams<{ eventId: string }>();

  // Add paywall hook
  const { unlocked, loading: paywallLoading, anonymousLink: paywallAnonymousLink } = useEventPaywall(eventId);

  // TAB STATE
  const [activeTab, setActiveTab] = useState<'guests' | 'add' | 'distribute'>('guests');
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  // FEEDBACK MESSAGE STATE
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // NEW state variables for controlling additional UI in the Add Guests tab and Send Options
  const [showDeviceContacts, setShowDeviceContacts] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // EVENT & RECIPIENTS STATE
  const [event, setEvent] = useState<EventData>({
    title: 'Loading...',
    date: '',
    location: ''
  });
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);

  // ----------------------------------------------------------------
  // UPDATED FILTERING AND SEARCH STATES
  // ----------------------------------------------------------------
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Newly added recipients (not yet saved to Firestore)
  const [newlyAddedRecipients, setNewlyAddedRecipients] = useState<Recipient[]>([]);

  // Invitation Distribution Settings
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [anonymousLink, setAnonymousLink] = useState('');
  const [allowAnyoneWithLink, setAllowAnyoneWithLink] = useState(true);
  const [reminderNoResponse, setReminderNoResponse] = useState('');
  const [reminderAll, setReminderAll] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');

  // Stats counts - Updated to include "unsent" and totalPeople
  const [pendingCount, setPendingCount] = useState(0);
  const [noResponseCount, setNoResponseCount] = useState(0);
  const [totalPeople, setTotalPeople] = useState(0);

  // ----------------------------------------------------------------
  // HELPER FUNCTION: Get Consistent Status Display Label
  // ----------------------------------------------------------------
  const getDisplayStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'unsent':
        return 'Pending';
      case 'sent':
        return 'Sent';
      case 'viewed':
        return 'Viewed';
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // ----------------------------------------------------------------
  // HELPER FUNCTION: Sync Status to Sub-Document
  // ----------------------------------------------------------------
  const syncStatusToSubDoc = async (
    invitationId: string,
    eventId: string,
    newStatus: string,
  ) => {
    try {
      const q = query(
        collection(db, 'events', eventId, 'invitations'),
        where('mainInvitationId', '==', invitationId),
        limit(1),
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        await updateDoc(snap.docs[0].ref, { status: newStatus });
      }
    } catch (error) {
      console.warn('Could not sync status to sub-document:', error);
    }
  };

  // ----------------------------------------------------------------
  // FIRESTORE DATA FETCHING (MAIN "invitations" COLLECTION ONLY)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!eventId) return;

    // ▸ 1. load event header details (unchanged)
    const fetchEventDetails = async () => {
      try {
        const eventRef = doc(db, 'events', eventId!);
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const eventData = eventDoc.data() as EventData;
          setEvent({
            title: eventData.title || 'Untitled Event',
            date: eventData.date || '',
            location: eventData.location || ''
          });
          // Use paywall anonymous link if available, otherwise fallback
          setAnonymousLink(
            paywallAnonymousLink ||
            eventData.anonymousInvitationLink ||
            `https://invites-platform.web.app/invite/${eventId}?id=${eventId}`
          );
          setPersonalMessage(eventData.personalMessage || '');
          // Load both reminder settings
          setReminderNoResponse(eventData.reminderNoResponse || '');
          setReminderAll(eventData.reminderAll || '');
          setAllowAnyoneWithLink(true);
          // Ensure that allowAnyoneWithLink is always true in Firestore
          await updateDoc(eventRef, { allowAnyoneWithLink: true });
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
      }
    };

    fetchEventDetails();

    // ▸ 2. real‑time guest list (deduped + latest status)
    const statusRank = (s: string) =>
      ['pending', 'unsent', 'sent', 'viewed', 'declined', 'accepted'].indexOf(
        s?.toLowerCase() || '',
      );

    const q = query(
      collection(db, 'invitations'),
      where('eventId', '==', eventId),
      where('recipientName', '!=', 'Anonymous'),
    );

    const unsubscribe = onSnapshot(q, snap => {
      const dedup = new Map<string, Recipient>();

      snap.forEach(docSnap => {
        const d = docSnap.data() as InvitationDocument;
        const key =
          (d.recipientEmail || d.recipientPhone || d.recipientName || '').toLowerCase();

        const rec: Recipient = {
          id: docSnap.id,
          name: d.recipientName || 'Guest',
          email: d.recipientEmail,
          phone: d.recipientPhone,
          status: (d.status || 'unknown').toLowerCase(),
          sent: d.sentAt
            ? new Date(d.sentAt.seconds * 1000).toLocaleDateString()
            : undefined,
          peopleCount: d.peopleCount || 1,
        };

        // keep only the highest‑ranked (latest) status per guest
        if (!dedup.has(key) || statusRank(rec.status) > statusRank(dedup.get(key)!.status)) {
          dedup.set(key, rec);
        }
      });

      setRecipients(Array.from(dedup.values()));
    });

    return () => unsubscribe();
  }, [eventId, paywallAnonymousLink]);

  // ----------------------------------------------------------------
  // FIX STATUS TRACKING - Update stats counts whenever recipients change
  // Include 'unsent' in the status list and calculate totalPeople
  // ----------------------------------------------------------------
  useEffect(() => {
    // Only include non-removed recipients
    const activeRecipients = recipients.filter(
      (r) => r.status.toLowerCase() !== 'removed'
    );
    setPendingCount(
      activeRecipients.filter((r) =>
        ['pending', 'unsent'].includes(r.status.toLowerCase())
      ).length
    );

    setNoResponseCount(
      activeRecipients.filter((r) =>
        ['pending', 'viewed', 'sent', 'unsent'].includes(r.status.toLowerCase())
      ).length
    );
    
    // Calculate total people
    const peopleSum = activeRecipients
      .filter(r => r.status.toLowerCase() === 'accepted')
      .reduce((sum, r) => sum + (r.peopleCount || 1), 0);
    setTotalPeople(peopleSum);
  }, [recipients]);

  // ----------------------------------------------------------------
  // UPDATED FILTERED RECIPIENTS FUNCTION USING LOGICAL GROUPINGS
  // ----------------------------------------------------------------
  const filteredRecipients = recipients.filter((recipient) => {
    const status = recipient.status.toLowerCase();
    let matchesStatus = false;
    switch (filterStatus) {
      case 'all':
        matchesStatus = true;
        break;
      case 'pending':
        // Include both 'pending' and 'unsent' in the pending filter
        matchesStatus = ['pending', 'unsent'].includes(status);
        break;
      case 'sent':
        // Anyone who has been sent an invitation (includes viewed/responded)
        matchesStatus = ['sent', 'viewed', 'accepted', 'declined'].includes(status);
        break;
      case 'viewed':
        // Only those who have viewed
        matchesStatus = status === 'viewed';
        break;
      case 'responded':
        // Anyone who has responded (accepted or declined)
        matchesStatus = ['accepted', 'declined'].includes(status);
        break;
      case 'accepted':
        matchesStatus = status === 'accepted';
        break;
      case 'declined':
        matchesStatus = status === 'declined';
        break;
      default:
        matchesStatus = false;
    }

    const lcSearch = searchTerm.toLowerCase();
    const matchesSearch =
      recipient.name.toLowerCase().includes(lcSearch) ||
      (recipient.email || '').toLowerCase().includes(lcSearch) ||
      (recipient.phone || '').toLowerCase().includes(lcSearch);

    return matchesStatus && matchesSearch;
  });

  // ----------------------------------------------------------------
  // CHECKBOX SELECTION FUNCTIONS
  // ----------------------------------------------------------------
  const handleToggleSelectAll = () => {
    if (
      selectedRecipients.length === filteredRecipients.length &&
      filteredRecipients.length > 0
    ) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(filteredRecipients.map((r) => r.id!));
    }
  };

  const handleToggleRecipient = (id: string) => {
    if (selectedRecipients.includes(id)) {
      setSelectedRecipients(selectedRecipients.filter((rId) => rId !== id));
    } else {
      setSelectedRecipients([...selectedRecipients, id]);
    }
  };

  // ----------------------------------------------------------------
  // BULK ACTION FUNCTIONS
  // ----------------------------------------------------------------
  const handleRemoveSelected = async () => {
    if (!eventId || selectedRecipients.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove ${selectedRecipients.length} selected guests?`)) return;
    
    try {
      for (const id of selectedRecipients) {
        // 1. mark the main invitation "removed" so the UI (and your counts) update
        await updateDoc(doc(db, 'invitations', id), { status: 'removed' });
        // 2. mirror to the sub‑collection – if it exists
        await updateDoc(doc(db, 'events', eventId!, 'invitations', id), { status: 'removed' })
          .catch(() => {/* ignore if legacy doc missing */});
      }
      alert('Selected guests have been removed.');
      setSelectedRecipients([]);
    } catch (err) {
      console.error('Error removing guests:', err);
      alert('Failed to remove selected guests.');
    }
  };

  // ----------------------------------------------------------------
  // ENHANCED RESEND FUNCTIONS WITH PAYWALL CHECK
  // ----------------------------------------------------------------
  const handleResendToSelected = async () => {
    if (!eventId || selectedRecipients.length === 0) return;

    // Check paywall
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setSendingInvitations(true);

    try {
      const recipientsToSend = recipients
        .filter((r) => selectedRecipients.includes(r.id!))
        .map((r) => ({
          name: r.name,
          email: r.email,
          phone: r.phone
        }))
        .filter((r) => r.email || r.phone); // Ensure we have at least one contact method

      if (recipientsToSend.length === 0) {
        throw new Error('Selected guests have no email or phone contact information.');
      }

      console.log(`Sending invitations to ${recipientsToSend.length} selected guests`);

      const sendInvitationsFunc = httpsCallable(functions, 'sendInvitations');
      const response = await sendInvitationsFunc({
        eventId,
        recipients: recipientsToSend,
        personalMessage
      });

      console.log('Cloud Function response:', response);
      setSuccessMessage(`Invitations resent to ${recipientsToSend.length} selected guests!`);
    } catch (error: any) {
      console.error('Error resending invitations:', error);
      if (error?.code === 'functions/failed-precondition') {
        setErrorMessage('Event distribution is locked. Please unlock to send invitations.');
      } else {
        setErrorMessage(`Failed to resend invitations: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleSingleRecipientResend = async (recipientId: string) => {
    if (!eventId) return;

    // Check paywall
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setSendingInvitations(true);

    try {
      const recipient = recipients.find((r) => r.id === recipientId);
      if (!recipient) {
        throw new Error('Guest not found');
      }

      if (!recipient.email && !recipient.phone) {
        throw new Error('Guest has no email or phone contact information');
      }

      const recipientToSend = {
        name: recipient.name,
        email: recipient.email,
        phone: recipient.phone
      };

      const sendInvitations = httpsCallable(functions, 'sendInvitations');
      const response = await sendInvitations({
        eventId,
        recipients: [recipientToSend],
        personalMessage
      });

      console.log('Single recipient invitation response:', response);
      setSuccessMessage(`Invitation resent to ${recipient.name}!`);
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      if (error?.code === 'functions/failed-precondition') {
        setErrorMessage('Event distribution is locked. Please unlock to send invitations.');
      } else {
        setErrorMessage(`Failed to resend the invitation: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleSingleRecipientDelete = async (recipientId: string) => {
    if (!eventId) return;
    const confirmed = window.confirm('Are you sure you want to remove this guest?');
    if (!confirmed) return;

    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // Update main collection first (this is what the page shows)
      const mainRef = doc(db, 'invitations', recipientId);
      await updateDoc(mainRef, { status: 'removed' });
      
      // Keep the sub‑collection in sync so reminders ignore the guest
      try {
        const subRef = doc(db, 'events', eventId!, 'invitations', recipientId);
        await updateDoc(subRef, { status: 'removed' });
      } catch (subError) {
        console.warn('Could not update subcollection (may not exist):', subError);
      }
      
      setSuccessMessage('Guest has been removed.');
    } catch (error) {
      console.error('Error removing guest:', error);
      setErrorMessage('Failed to remove guest. Please try again.');
    }
  };

  // ----------------------------------------------------------------
  // ADD GUESTS FUNCTIONS
  // ----------------------------------------------------------------
  const saveNewGuests = async () => {
    if (!eventId || newlyAddedRecipients.length === 0) return;
    try {
      const subRef  = collection(db, 'events', eventId!, 'invitations'); // legacy
      const mainRef = collection(db, 'invitations');                    // canonical

      await Promise.all(
        newlyAddedRecipients.map(async r => {
          const base = {
            eventId,
            recipientName : r.name,
            recipientEmail: r.email || '',
            recipientPhone: r.phone || '',
            status       : 'pending',
            peopleCount  : r.peopleCount || 1,
            createdAt    : serverTimestamp(),
          };
          // 1. create the main doc first so we get its ID
          const mainDoc = await addDoc(mainRef, base);
          // 2. keep the legacy sub‑collection in sync *under the same doc‑id*
          await setDoc(doc(subRef, mainDoc.id), {
            ...base,
            mainInvitationId: mainDoc.id, // still handy for back‑end jobs
          });
        }),
      );

      setRecipients(prev => [...prev, ...newlyAddedRecipients]);
      setNewlyAddedRecipients([]);
    } catch (error) {
      console.error('Error saving new guests:', error);
      alert('Failed to save new guests. Please try again.');
    }
  };

  // Adds new recipients to the locally saved list; ensures each has a status
  const handleAddNewRecipients = (newOnes: any[]) => {
    const recipientsWithStatus: Recipient[] = newOnes.map((r) => ({
      ...r,
      status: r.status || 'pending',
      peopleCount: r.peopleCount || 1  // Ensure peopleCount is set
    }));
    setNewlyAddedRecipients((prev) => [...prev, ...recipientsWithStatus]);
  };

  // ----------------------------------------------------------------
  // ENHANCED SEND INVITATIONS FUNCTION WITH PAYWALL CHECK
  // ----------------------------------------------------------------
  const handleSendInvitations = async (type: 'all' | 'pending' | 'noResponse') => {
    console.log(`Button clicked: ${type} - noResponseCount: ${noResponseCount}`);

    if (!eventId) return;

    // Check paywall
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }

    setSendingInvitations(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      console.log('ManageGuestsPage.tsx - Sending invitations:');
      console.log('- type:', type);
      console.log('- eventId:', eventId);
      console.log('- personalMessage:', personalMessage ? 'present' : 'not set');

      // Fetch the appropriate recipients based on type
      let recipientsArray: { name: string; email?: string; phone?: string }[] = [];

      let q: Query<unknown>;
      const invitationsRef = collection(db, 'events', eventId!, 'invitations');

      if (type === 'all') {
        // All recipients except ones marked as removed
        q = query(invitationsRef, where('status', '!=', 'removed'));
        console.log('Querying for all non-removed recipients');
      } else if (type === 'pending') {
        // Only pending recipients
        q = query(invitationsRef, where('status', '==', 'pending'));
        console.log('Querying for pending recipients');
      } else if (type === 'noResponse') {
        // No response recipients - include 'unsent'
        console.log('Querying for unresponsive statuses: pending, viewed, sent, unsent');
        q = query(
          invitationsRef,
          where('status', 'in', ['pending', 'viewed', 'sent', 'unsent'])
        );
      } else {
        throw new Error('Invalid invitation type');
      }

      const snapshot = await getDocs(q);
      console.log(`Query returned ${snapshot.docs.length} docs`);

      snapshot.docs.forEach((doc) => {
        const data = doc.data() as {
          recipientName: string;
          recipientEmail?: string;
          recipientPhone?: string;
          status: string;
        };
        console.log(`Recipient: ${data.recipientName}, Status: ${data.status}`);
      });

      recipientsArray = snapshot.docs
        .map((doc) => {
          const data = doc.data() as {
            recipientName?: string;
            recipientEmail?: string;
            recipientPhone?: string;
          };
          return {
            name: data.recipientName || 'Guest',
            email: data.recipientEmail || undefined,
            phone: data.recipientPhone || undefined
          };
        })
        .filter((recipient) => recipient.email || recipient.phone);

      console.log(`- Found ${recipientsArray.length} valid recipients to send to`);

      // Call the cloud function
      const sendInvitations = httpsCallable(functions, 'sendInvitations');
      const response = await sendInvitations({
        eventId,
        recipients: recipientsArray,
        personalMessage
      });

      console.log('Complete Cloud Function response:', response);
      console.log('ManageGuestsPage.tsx - Cloud Function response:', response);

      // After successfully calling the Cloud Function, synchronize subcollection statuses
      const data = response.data as SendInvitationsResponse;
      if (data && data.invitationIds) {
        console.log('Updating subcollection statuses for sent invitations');
        try {
          for (const id of data.invitationIds) {
            const mainInviteRef = doc(db, 'invitations', id);
            const mainInviteDoc = await getDoc(mainInviteRef);

            if (mainInviteDoc.exists()) {
              const mainData = mainInviteDoc.data();
              const mainStatus = mainData.status;

              const matchingRecipient = recipients.find(
                (r) =>
                  (mainData.recipientEmail && r.email === mainData.recipientEmail) ||
                  (mainData.recipientPhone && r.phone === mainData.recipientPhone)
              );

              if (matchingRecipient && matchingRecipient.id) {
                const subInviteRef = doc(
                  db,
                  'events',
                  eventId,
                  'invitations',
                  matchingRecipient.id
                );
                await updateDoc(subInviteRef, { status: mainStatus });
                console.log(
                  `Updated subcollection status for ${matchingRecipient.name} to ${mainStatus}`
                );
              }
            }
          }
        } catch (syncError) {
          console.error('Error syncing invitation statuses:', syncError);
        }
      }

      setSuccessMessage(
        `Invitations ${
          type === 'all'
            ? 'sent to all guests'
            : type === 'pending'
            ? 'sent to new guests'
            : 'reminders sent to unresponsive guests'
        } successfully!`
      );
    } catch (error: any) {
      console.error('ManageGuestsPage.tsx - Error sending invitations:', error);
      if (error.code) {
        console.error('Error code:', error.code);
      }
      if (error.message) {
        console.error('Error message:', error.message);
      }

      if (error?.code === 'functions/failed-precondition') {
        setErrorMessage('Event distribution is locked. Please unlock to send invitations.');
      } else {
        setErrorMessage(`Failed to send invitations: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleCopyLink = async () => {
    // Check paywall
    if (!unlocked) {
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
      return;
    }

    try {
      await navigator.clipboard.writeText(anonymousLink);
      alert('Invitation link copied to clipboard!');
    } catch (error) {
      console.error('Error copying link:', error);
      alert('Failed to copy link.');
    }
  };

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <SparkLayout>
      {/* Add UnlockSalesModal that listens for global events */}
      {eventId && <UnlockSalesModal eventId={eventId} listenGlobalOpen={true} />}
      
      <SparkCard style={{
        width: '90%',
        maxWidth: '64rem',
        margin: '0 auto 6rem auto',
      }}>
        <SparkHeading level="h1" glow>
          Manage Guests: {event.title}
        </SparkHeading>
        
        {/* Paywall Banner */}
        {!paywallLoading && !unlocked && (
          <SparkCard variant="solid" style={{ marginBottom: '1rem', border: '3px solid #000', backgroundColor: '#fff3cd' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', padding: '1rem' }}>
              <div style={{ color: '#856404', fontWeight: 900, fontSize: 18, textAlign: 'center' }}>
                🔒 Distribution is locked for this event
              </div>
              <div style={{ color: '#856404', fontSize: 14, textAlign: 'center' }}>
                Unlock to send invitations, copy links, and enable automated reminders
              </div>
              <SparkButton 
                onClick={() => window.dispatchEvent(new CustomEvent('spark:openUnlockModal'))}
                size="large"
                style={{ marginTop: '0.5rem' }}
              >
                UNLOCK DISTRIBUTION - $4.99
              </SparkButton>
            </div>
          </SparkCard>
        )}
        
        {/* Feedback Message Display */}
        {successMessage && (
          <div className="spark-message spark-message--success">
            <p>{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="spark-message spark-message--error">
            <p>{errorMessage}</p>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          marginBottom: '2px',
        }}>
          <SparkButton
            onClick={() => setActiveTab('guests')}
            variant={activeTab === 'guests' ? 'violet' : 'default'}
            style={{
              borderRadius: '0.5rem 0.5rem 0 0',
              margin: '0 0.5rem',
              position: 'relative',
              zIndex: activeTab === 'guests' ? 2 : 1,
              marginBottom: activeTab === 'guests' ? '-4px' : '0',
            }}
          >
            Guest List
          </SparkButton>
          <SparkButton
            onClick={() => setActiveTab('add')}
            variant={activeTab === 'add' ? 'violet' : 'default'}
            style={{
              borderRadius: '0.5rem 0.5rem 0 0',
              margin: '0 0.5rem',
              position: 'relative',
              zIndex: activeTab === 'add' ? 2 : 1,
              marginBottom: activeTab === 'add' ? '-4px' : '0',
            }}
          >
            Add Guests
          </SparkButton>
          <SparkButton
            onClick={() => setActiveTab('distribute')}
            variant={activeTab === 'distribute' ? 'violet' : 'default'}
            style={{
              borderRadius: '0.5rem 0.5rem 0 0',
              margin: '0 0.5rem',
              position: 'relative',
              zIndex: activeTab === 'distribute' ? 2 : 1,
              marginBottom: activeTab === 'distribute' ? '-4px' : '0',
            }}
          >
            Send & Share
          </SparkButton>
        </div>

        <div style={{
          borderRadius: '0 0 0.5rem 0.5rem',
          padding: '1rem',
          width: '100%',
        }}>
          {activeTab === 'guests' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <SparkHeading level="h3" style={{ marginBottom: 0 }}>
                  Guest List ({filteredRecipients.length})
                </SparkHeading>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {selectedRecipients.length > 0 && (
                    <>
                      <SparkButton onClick={handleResendToSelected} size="small">
                        {unlocked ? 'RESEND' : '🔒 UNLOCK'}
                      </SparkButton>
                      <SparkButton onClick={handleRemoveSelected} size="small">
                        REMOVE
                      </SparkButton>
                    </>
                  )}
                </div>
              </div>
              
              {/* Filter and search row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <SparkInput
                    id="filterStatus"
                    type="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                    style={{ width: '200px' }}
                  >
                    <option value="all">All Guests</option>
                    <option value="pending">Not Yet Sent</option>
                    <option value="sent">Invitation Sent</option>
                    <option value="viewed">Viewed (No Response)</option>
                    <option value="responded">Responded</option>
                    <option value="accepted">Accepted</option>
                    <option value="declined">Declined</option>
                  </SparkInput>
                </div>
                <div style={{ flexGrow: 1 }}>
                  <SparkInput
                    id="searchGuests"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search guests..."
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="spark-table" style={{ minWidth: '700px' }}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            style={{ width: '1rem', height: '1rem', border: '2px solid rgba(255, 255, 255, 0.3)' }}
                            checked={
                              selectedRecipients.length === filteredRecipients.length &&
                              filteredRecipients.length > 0
                            }
                            onChange={handleToggleSelectAll}
                          />
                        </th>
                        <th>Guest</th>
                        <th style={{ textAlign: 'center', width: '100px' }}>
                          Status
                        </th>
                        <th style={{ textAlign: 'center', width: '80px' }}>
                          People
                        </th>
                        <th style={{ textAlign: 'center', width: '80px' }}>
                          Sent
                        </th>
                        <th style={{ textAlign: 'center', width: '140px' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecipients.map((recipient) => (
                        <tr key={recipient.id}>
                          <td>
                            <input
                              type="checkbox"
                              style={{ width: '1rem', height: '1rem', border: '2px solid rgba(255, 255, 255, 0.3)' }}
                              checked={selectedRecipients.includes(recipient.id!)}
                              onChange={() => handleToggleRecipient(recipient.id!)}
                            />
                          </td>
                          <td>
                            <div style={{ fontWeight: 'bold', color: 'var(--color-text-primary)' }}>{recipient.name}</div>
                            <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                              {recipient.email}{' '}
                              {recipient.phone && ` / ${recipient.phone}`}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                backgroundColor:
                                  recipient.status.toLowerCase() === 'accepted'
                                    ? 'var(--color-success)'
                                    : recipient.status.toLowerCase() === 'declined'
                                    ? 'var(--color-error)'
                                    : recipient.status.toLowerCase() === 'viewed'
                                    ? '#3b82f6'
                                    : recipient.status.toLowerCase() === 'sent'
                                    ? 'var(--color-accent-violet)'
                                    : 'var(--color-accent-yellow)',
                                color: 'white'
                              }}
                            >
                              {getDisplayStatusLabel(recipient.status)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontSize: '0.875rem', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>
                            {recipient.status.toLowerCase() === 'accepted' ? (recipient.peopleCount || 1) : '--'}
                          </td>
                          <td style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>
                            {recipient.sent || '--'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.25rem' }}>
                              <SparkButton
                                onClick={() => handleSingleRecipientResend(recipient.id!)}
                                size="small"
                                style={{ minWidth: '60px' }}
                              >
                                {unlocked ? 'Resend' : '🔒'}
                              </SparkButton>
                              <SparkButton
                                onClick={() => handleSingleRecipientDelete(recipient.id!)}
                                size="small"
                                style={{ minWidth: '60px' }}
                              >
                                Delete
                              </SparkButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredRecipients.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
                            No guests found matching your filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '90%',
                margin: '2.5rem auto 1.5rem auto',
                maxWidth: '700px'
              }}>
                <div className="spark-stats-box" style={{ backgroundColor: 'var(--color-surface-glass)' }}>
                  <div className="spark-stats-box__value">{recipients.length}</div>
                  <div className="spark-stats-box__label">Total Invited</div>
                </div>
                <div className="spark-stats-box" style={{ backgroundColor: 'rgba(74, 222, 128, 0.2)' }}>
                  <div className="spark-stats-box__value">
                    {
                      recipients.filter((r) => r.status.toLowerCase() === 'accepted')
                        .length
                    }
                  </div>
                  <div className="spark-stats-box__label">Accepted</div>
                </div>
                <div className="spark-stats-box" style={{ backgroundColor: 'rgba(255, 112, 112, 0.2)' }}>
                  <div className="spark-stats-box__value">
                    {
                      recipients.filter((r) => r.status.toLowerCase() === 'declined')
                        .length
                    }
                  </div>
                  <div className="spark-stats-box__label">Declined</div>
                </div>
                <div className="spark-stats-box" style={{ backgroundColor: 'rgba(255, 238, 109, 0.2)' }}>
                  <div className="spark-stats-box__value">
                    {
                      recipients.filter((r) =>
                        ['pending', 'unsent'].includes(r.status.toLowerCase())
                      ).length
                    }
                  </div>
                  <div className="spark-stats-box__label">Pending</div>
                </div>
                <div className="spark-stats-box" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }}>
                  <div className="spark-stats-box__value">{totalPeople}</div>
                  <div className="spark-stats-box__label">People</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '2rem' }}>
                <SparkButton to={`/event/${eventId}`}>
                  BACK TO EVENT
                </SparkButton>
                <SparkButton onClick={() => setActiveTab('add')}>
                  ADD GUESTS
                </SparkButton>
              </div>
            </div>
          )}

          {activeTab === 'add' && (
            <div>
              <SparkHeading level="h3">Add Guests</SparkHeading>
              <p className="text-center mb-4 text-sm opacity-75">
                Texting is coming soon!
              </p>
              {/* Device Contacts */}
              <div style={{ marginBottom: '1rem' }}>
                <SparkButton
                  onClick={() => setShowDeviceContacts(true)}
                  fullWidth
                >
                  ADD FROM DEVICE CONTACTS
                </SparkButton>
              </div>
              {showDeviceContacts && (
                <SparkCard variant="solid" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: 0 }}>
                      Import from Device
                    </SparkHeading>
                    <SparkButton
                      onClick={() => setShowDeviceContacts(false)}
                      size="small"
                    >
                      HIDE
                    </SparkButton>
                  </div>
                  <DeviceContactPicker
                    onContactsSelected={(selectedContacts) => {
                      const newRecipients = selectedContacts.map((contact) => ({
                        name: contact.name,
                        email: contact.email,
                        phone: contact.phone,
                        status: 'pending'
                      }));
                      handleAddNewRecipients(newRecipients);
                      setShowDeviceContacts(false);
                    }}
                  />
                </SparkCard>
              )}

              {/* CSV Import */}
              <div style={{ marginBottom: '1rem' }}>
                <SparkButton
                  onClick={() => setShowCsvImport(true)}
                  fullWidth
                >
                  IMPORT FROM CSV
                </SparkButton>
              </div>
              {showCsvImport && (
                <SparkCard variant="solid" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: 0 }}>
                      Import Recipients
                    </SparkHeading>
                    <SparkButton
                      onClick={() => setShowCsvImport(false)}
                      size="small"
                    >
                      HIDE
                    </SparkButton>
                  </div>
                  <ImportRecipients
                    onImportComplete={(imported) => {
                      handleAddNewRecipients(imported);
                      setShowCsvImport(false);
                    }}
                  />
                </SparkCard>
              )}

              {/* Manual Entry */}
              <div style={{ marginBottom: '1rem' }}>
                <SparkButton
                  onClick={() => setShowManualEntry(true)}
                  fullWidth
                >
                  ADD MANUALLY
                </SparkButton>
              </div>
              {showManualEntry && (
                <SparkCard variant="solid" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: 0 }}>
                      Add Recipient Manually
                    </SparkHeading>
                    <SparkButton
                      onClick={() => setShowManualEntry(false)}
                      size="small"
                    >
                      HIDE
                    </SparkButton>
                  </div>
                  <ManualEntryForm
                    onAddRecipient={(recipient) => {
                      handleAddNewRecipients([recipient]);
                    }}
                  />
                </SparkCard>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '2rem' }}>
                <SparkButton onClick={() => setActiveTab('guests')}>
                  BACK
                </SparkButton>
                <SparkButton
                  onClick={() => {
                    // If there are new recipients, save them first
                    if (newlyAddedRecipients.length > 0) {
                      saveNewGuests();
                    }
                    // Proceed to the next tab either way
                    setActiveTab('distribute');
                  }}
                  disabled={newlyAddedRecipients.length === 0 && recipients.length === 0}
                >
                  SEND & SHARE
                </SparkButton>
              </div>
            </div>
          )}

          {activeTab === 'distribute' && (
            <div>
              <SparkHeading level="h3">
                Send & Share Invitations
              </SparkHeading>
              
              {/* Show Send Invitations button only when the card is not open */}
              {!showSendOptions && (
                <div style={{ marginBottom: '1rem' }}>
                  <SparkButton
                    onClick={() => setShowSendOptions(true)}
                    fullWidth
                  >
                    SEND INVITATIONS
                  </SparkButton>
                </div>
              )}

              {/* Conditional rendering of send options */}
              {showSendOptions && (
                <SparkCard variant="solid" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none', marginBottom: 0, marginRight: '0.75rem' }}>
                      Send Options {!unlocked && '(🔒 Locked)'}
                    </SparkHeading>
                    <SparkButton
                      onClick={() => setShowSendOptions(false)}
                      size="small"
                    >
                      HIDE
                    </SparkButton>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', width: '100%' }}>
                    <SparkButton
                      onClick={() => handleSendInvitations('all')}
                      disabled={!unlocked || sendingInvitations || recipients.length === 0}
                      fullWidth
                      style={{ height: '60px' }}
                    >
                      {sendingInvitations
                        ? 'SENDING...'
                        : `SEND TO ALL GUESTS (${recipients.length})`}
                    </SparkButton>
                    <SparkButton
                      onClick={() => handleSendInvitations('pending')}
                      disabled={!unlocked || sendingInvitations || pendingCount === 0}
                      fullWidth
                      style={{ height: '60px' }}
                    >
                      {sendingInvitations
                        ? 'SENDING...'
                        : `SEND TO NEW GUESTS (${pendingCount})`}
                    </SparkButton>
                    <SparkButton
                      onClick={() => handleSendInvitations('noResponse')}
                      disabled={!unlocked || sendingInvitations || noResponseCount === 0}
                      fullWidth
                      style={{ height: '60px' }}
                    >
                      {sendingInvitations
                        ? 'SENDING...'
                        : `POKE UNRESPONSIVES (${noResponseCount})`}
                    </SparkButton>
                    {/* Copy invitation link button */}
                    <SparkButton
                      onClick={handleCopyLink}
                      disabled={!unlocked}
                      fullWidth
                      style={{ height: '60px' }}
                    >
                      COPY INVITATION LINK
                    </SparkButton>
                  </div>
                </SparkCard>
              )}

              <SparkCard variant="solid" style={{ marginBottom: '1.5rem' }}>
                <SparkHeading level="h4" style={{ color: '#000', textShadow: 'none' }}>
                  Automated Reminders {!unlocked && '(🔒 Locked)'}
                </SparkHeading>
                <SparkInput
                  label="Send reminder to guests who haven't responded"
                  id="reminderNoResponse"
                  type="select"
                  value={reminderNoResponse}
                  onChange={(e) =>
                    (async () => {
                      if (!unlocked) {
                        window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
                        return;
                      }
                      try {
                        const eventRef = doc(db, 'events', eventId!);
                        setReminderNoResponse(e.target.value);
                        await updateDoc(eventRef, {
                          reminderNoResponse: e.target.value
                        });
                      } catch (error) {
                        console.error('Error updating reminder settings:', error);
                        alert('Failed to update reminder settings. Please try again.');
                      }
                    })()
                  }
                  disabled={!unlocked}
                >
                  <option value="">No automatic reminders</option>
                  <option value="1d">1 day before event</option>
                  <option value="3d">3 days before event</option>
                  <option value="1w">1 week before event</option>
                  <option value="2w">2 weeks before event</option>
                </SparkInput>
                <SparkInput
                  label="Send event reminder to all guests"
                  id="reminderAll"
                  type="select"
                  value={reminderAll}
                  onChange={(e) =>
                    (async () => {
                      if (!unlocked) {
                        window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
                        return;
                      }
                      try {
                        const eventRef = doc(db, 'events', eventId!);
                        setReminderAll(e.target.value);
                        await updateDoc(eventRef, {
                          reminderAll: e.target.value
                        });
                      } catch (error) {
                        console.error('Error updating reminder settings:', error);
                        alert('Failed to update reminder settings. Please try again.');
                      }
                    })()
                  }
                  disabled={!unlocked}
                >
                  <option value="">No reminder</option>
                  <option value="0d">On the day of the event</option>
                  <option value="1d">1 day before event</option>
                  <option value="3d">3 days before event</option>
                  <option value="1w">1 week before event</option>
                </SparkInput>
              </SparkCard>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '2rem' }}>
                <SparkButton onClick={() => setActiveTab('add')}>
                  BACK
                </SparkButton>
                <SparkButton to={`/event/${eventId}`}>
                  DONE
                </SparkButton>
              </div>
            </div>
          )}
        </div>
      </SparkCard>
      <SparkFooter />
    </SparkLayout>
  );
};

export default ManageGuestsPage;